<script setup lang="ts">
import { Activity, CircleAlert, Coins, LoaderCircle, Radio, Users } from "lucide-vue-next"
import { computed, onMounted, ref } from "vue"

import { apiRequest } from "../../lib/api"
import type { AdminOverview, ProviderAccountItem } from "../../lib/types"

const overview = ref<AdminOverview | null>(null)
const accounts = ref<ProviderAccountItem[]>([])
const loading = ref(true)
const error = ref("")

const failureRate = computed(() => {
  if (!overview.value?.calls_24h) return "0.0%"
  return `${((overview.value.failed_calls_24h / overview.value.calls_24h) * 100).toFixed(1)}%`
})

onMounted(async () => {
  try {
    ;[overview.value, accounts.value] = await Promise.all([
      apiRequest<AdminOverview>("/v1/admin/overview"),
      apiRequest<ProviderAccountItem[]>("/v1/admin/provider-accounts"),
    ])
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "加载运营数据失败"
  } finally {
    loading.value = false
  }
})

function formatNumber(value: number | undefined): string {
  return value?.toLocaleString("zh-CN") ?? "—"
}
</script>

<template>
  <section class="admin-page">
    <div class="admin-page-heading">
      <div>
        <span class="eyebrow">System pulse</span>
        <h1>运营概览</h1>
        <p>用户、积分、接口调用和平台账号的实时控制面。</p>
      </div>
      <span class="admin-live"><i></i> 数据实时</span>
    </div>

    <div v-if="loading" class="admin-loading"><LoaderCircle class="spin" :size="20" />正在汇总系统数据</div>
    <p v-else-if="error" class="form-error">{{ error }}</p>
    <template v-else>
      <div class="admin-metric-grid">
        <article><span><Users :size="17" />用户</span><strong>{{ formatNumber(overview?.total_users) }}</strong><small>{{ overview?.active_users }} 个活跃账户</small></article>
        <article><span><Coins :size="17" />可用额度</span><strong>{{ formatNumber(overview?.credits) }}</strong><small>全部 Workspace</small></article>
        <article><span><Activity :size="17" />24h 调用</span><strong>{{ formatNumber(overview?.calls_24h) }}</strong><small>失败率 {{ failureRate }}</small></article>
        <article><span><Radio :size="17" />在池账号</span><strong>{{ overview?.pool_accounts ?? 0 }} / {{ overview?.provider_accounts ?? 0 }}</strong><small>{{ overview?.logged_in_accounts }} 个已登录</small></article>
      </div>

      <div class="admin-overview-grid">
        <section class="admin-panel">
          <header><div><span class="eyebrow">Provider health</span><h2>平台账号状态</h2></div><RouterLink to="/admin/provider-accounts" class="inline-link">管理号池 →</RouterLink></header>
          <div v-if="accounts.length" class="provider-health-list">
            <article v-for="account in accounts.slice(0, 8)" :key="account.id">
              <span class="platform-mark" :class="`platform-mark--${account.platform}`">{{ { rednote: "RED", zhihu: "知", douban: "豆", douyin: "抖", weibo: "博", weibo_web: "WEB", bilibili: "哔", xueqiu: "雪", reddit: "R", twitter: "X", xianyu: "闲", jd: "京", nowcoder: "牛", instagram: "IG", maimai: "脉", jianyu: "剑", dongchedi: "车", ctrip: "旅", linkedin: "in" }[account.platform] }}</span>
              <div><strong>{{ account.display_name }}</strong><code>{{ account.state_name }}</code></div>
              <span class="status-pill" :class="{ 'status-pill--active': account.login_status === 'logged_in' }"><i></i>{{ account.login_status }}</span>
              <small>权重 {{ account.weight }}</small>
            </article>
          </div>
          <div v-else class="admin-empty">还没有平台账号，先创建并完成登录。</div>
        </section>

        <section class="admin-panel admin-risk-panel">
          <header><div><span class="eyebrow">Attention</span><h2>需要关注</h2></div><CircleAlert :size="18" /></header>
          <div class="risk-row"><span>24 小时失败调用</span><strong>{{ overview?.failed_calls_24h ?? 0 }}</strong></div>
          <div class="risk-row"><span>未登录平台账号</span><strong>{{ (overview?.provider_accounts ?? 0) - (overview?.logged_in_accounts ?? 0) }}</strong></div>
          <div class="risk-row"><span>未进入号池账号</span><strong>{{ (overview?.provider_accounts ?? 0) - (overview?.pool_accounts ?? 0) }}</strong></div>
          <p>账号异常不会自动重放请求。请先检查错误，再恢复健康状态。</p>
        </section>
      </div>
    </template>
  </section>
</template>
